import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, PencilSimple, Trash, FloppyDisk, X } from "@phosphor-icons/react";
import { useSector } from "@/lib/sectorContext";

// v0.98 · Fev/2026 — Categorias nativas do setor automotivo. Para
// outros verticais, usamos um conjunto genérico (Principal / Extra /
// Pacote / Outros) — evita que uma Clínica ou uma Igreja veja chips
// como "Revisão" ou "Diagnóstico".
const CATEGORIES_AUTOMOTIVE = [
  { value: "manutencao", label: "Manutenção" },
  { value: "revisao", label: "Revisão" },
  { value: "estetica", label: "Estética" },
  { value: "comercial", label: "Comercial" },
  { value: "diagnostico", label: "Diagnóstico" },
  { value: "outros", label: "Outros" },
];

const CATEGORIES_GENERIC = [
  { value: "principal", label: "Principal" },
  { value: "extra", label: "Extra / Adicional" },
  { value: "pacote", label: "Pacote / Combo" },
  { value: "outros", label: "Outros" },
];

const emptyService = {
  code: "",
  name: "",
  description: "",
  duration_min: 60,
  price_cents: 0,
  active: true,
  category: "outros",
};

function brl(cents) {
  return ((cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", minimumFractionDigits: 2,
  });
}

export default function ServicesTab({ onChange, onToast }) {
  const { isAutomotive, labels } = useSector();
  const CATEGORIES = isAutomotive ? CATEGORIES_AUTOMOTIVE : CATEGORIES_GENERIC;
  const serviceSingular = labels?.serviceSingular?.(false) || "serviço";
  const serviceSingularCap = labels?.serviceSingular?.(true) || "Serviço";
  const defaultCategory = isAutomotive ? "outros" : "principal";
  const codePlaceholder = isAutomotive ? "Código (ex.: oil_change)" : "Código (ex.: cons_padrao)";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/services");
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      onToast?.("Erro ao carregar serviços.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.code?.trim() || !editing.name?.trim()) {
      onToast?.("Código e nome obrigatórios.", "error");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: editing.code.trim(),
        name: editing.name.trim(),
        description: editing.description || "",
        duration_min: Number(editing.duration_min) || 60,
        price_cents: Number(editing.price_cents) || 0,
        active: editing.active !== false,
        category: editing.category || defaultCategory,
      };
      if (editing.id) {
        await api.put(`/services/${editing.id}`, payload);
        onToast?.("Serviço atualizado.");
      } else {
        await api.post("/services", payload);
        onToast?.("Serviço criado.");
      }
      setEditing(null);
      await load();
      onChange?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Erro ao salvar.", "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este serviço?")) return;
    try {
      await api.delete(`/services/${id}`);
      onToast?.("Serviço removido.");
      await load();
      onChange?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Erro ao remover.", "error");
    }
  };

  if (loading) return <div className="text-sm text-slate-400">Carregando…</div>;

  return (
    <div className="space-y-6" data-testid="services-tab">
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-slate-500">
          {items.length} serviço{items.length === 1 ? "" : "s"} no catálogo.
        </p>
        <button
          type="button"
          data-testid="services-add"
          onClick={() => setEditing({ ...emptyService })}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[13px]"
        >
          <Plus size={12} weight="bold" /> Novo serviço
        </button>
      </div>

      {items.length === 0 ? (
        <div className="px-6 py-10 rounded-2xl border border-dashed border-slate-200 text-center">
          <p className="text-sm text-slate-500">Nenhum serviço cadastrado.</p>
          <p className="text-[12px] text-slate-400 mt-1">
            Sem catálogo, a IA não cita preço — apenas confirma com a equipe.
          </p>
        </div>
      ) : (
        <div className="border border-slate-100 rounded-2xl overflow-hidden divide-y divide-slate-100">
          {items.map((s) => (
            <div
              key={s.id}
              data-testid={`service-row-${s.id}`}
              className="px-5 py-4 flex items-center gap-4 hover:bg-slate-50"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-900 truncate">{s.name}</span>
                  {s.active === false && (
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 border border-slate-200 rounded-full px-1.5 py-0.5">
                      inativo
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-slate-500 mt-0.5 truncate">
                  <span className="font-mono">{s.code}</span>
                  {s.description && ` · ${s.description}`}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-semibold text-slate-900 tabular-nums">
                  {brl(s.price_cents)}
                </div>
                <div className="text-[11px] text-slate-400">{s.duration_min} min</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => setEditing(s)}
                  className="p-2 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                  data-testid={`service-edit-${s.id}`}
                  aria-label="editar"
                >
                  <PencilSimple size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => remove(s.id)}
                  className="p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-rose-600"
                  data-testid={`service-remove-${s.id}`}
                  aria-label="remover"
                >
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-6" data-testid="service-modal">
          <div className="w-full max-w-lg bg-white rounded-3xl shadow-xl">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">
                {editing.id ? "Editar serviço" : "Novo serviço"}
              </h3>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="p-1 rounded-full hover:bg-slate-100 text-slate-500"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder={codePlaceholder}
                  value={editing.code}
                  onChange={(e) => setEditing({ ...editing, code: e.target.value })}
                  className="px-3 py-2 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none font-mono"
                  data-testid="service-modal-code"
                />
                <select
                  value={editing.category}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className="px-3 py-2 rounded-full border border-slate-200 text-[13px] bg-white focus:border-slate-900 focus:outline-none"
                  data-testid="service-modal-category"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <input
                type="text"
                placeholder="Nome (visível à IA)"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                className="w-full px-3 py-2 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none"
                data-testid="service-modal-name"
              />
              <textarea
                placeholder="Descrição curta (opcional)"
                value={editing.description || ""}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 rounded-2xl border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none resize-none"
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-slate-500 uppercase tracking-wider">Preço</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0,00"
                    value={editing.price_cents ? (editing.price_cents / 100) : ""}
                    onChange={(e) => setEditing({
                      ...editing,
                      price_cents: Math.round((parseFloat(e.target.value) || 0) * 100),
                    })}
                    className="w-full mt-1 px-3 py-2 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none tabular-nums"
                    data-testid="service-modal-price"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-slate-500 uppercase tracking-wider">Duração (min)</label>
                  <input
                    type="number"
                    min="1"
                    value={editing.duration_min}
                    onChange={(e) => setEditing({ ...editing, duration_min: parseInt(e.target.value, 10) || 0 })}
                    className="w-full mt-1 px-3 py-2 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none tabular-nums"
                    data-testid="service-modal-duration"
                  />
                </div>
              </div>
              <label className="inline-flex items-center gap-2 text-[13px] text-slate-700">
                <input
                  type="checkbox"
                  checked={editing.active !== false}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                />
                Ativo (a IA pode oferecer)
              </label>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="px-4 py-2 rounded-full text-slate-600 hover:bg-slate-100 text-[13px]"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="service-modal-save"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[13px] disabled:opacity-50"
              >
                <FloppyDisk size={12} weight="bold" />
                {saving ? "Salvando…" : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
